import React, { useState, Fragment, useEffect } from "react";
import { Accordion, AccordionBody, AccordionHeader, Button, Option, Select, Spinner } from "@material-tailwind/react";
import { ArchiveXIcon, ChevronDownIcon, ChevronUpIcon, ClipboardClockIcon, UserIcon, XIcon } from "lucide-react";
import { DeleteContract } from "../../services/backend";
import moment from "moment";
import { convertToPresentableText, Notify, NotifyError } from "../../utils/functions";
import { STATIC_USER_ROLES, VERIFICATION_TYPES, VERIFICATION_TYPES_ENUM } from "../../utils/enums";
import { useConfirm } from "../../custom-hooks/confirm-dialog";
import ContractFormModal from "./contractFormModal";
import ContractTerminateModal from "./contractTerminateModal";
import { useGetContracts } from "../../custom-hooks/use-contracts";
import Autocomplete from "../../components/autocomplete";
import { useGetUsers } from "../../custom-hooks/use-users";
import { InterpretVerificationStatus } from "../../utils/ui-interpreters";
import PageHeader from "../../components/page-header";
import { Pagination } from "../../components";


const INITIAL_FORM_DATA = {
    // Basic Information
    user: '',
    contractType: 'permanent',
    startDate: '',
    endDate: '',
    status: VERIFICATION_TYPES.APPROVED,
    comment: '',
    reason: '',
    duration: 0,
    noticePeriod: 4,
    selectedOffices: [],
    selectedSeats: [],

    totalAdvanceRent: 0,
    securityDeposit: 0,
    oneTimeServiceCharges: 0,
    otherCharges: [],
};

const ContractPage = () => {

    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [isUpdating, setIsUpdating] = useState(false);
    const [open, setOpen] = useState(-1);
    const [loadingDeleteContract, setLoadingDeleteContract] = useState(false);
    const [contractDataToTerminate, setContractDataToTerminate] = useState(null);
    const [teminationModalOpen, setTeminationModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        user: '',
        status: '',
    });
    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const { confirm, ConfirmationDialog } = useConfirm();

    const { data: contracts, isLoading: isContractsLoading, isError: isContractsError } = useGetContracts({
        ...filters,
        user: filters.user?.value || '',
        _page: page,
        _limit: limit,

    });

    const {
        total,
        totalPages,
    } = contracts || {};

    const { data: users, isLoading: isUsersLoading, isError: isUsersError } = useGetUsers({
        _limit: 1000,
        role: STATIC_USER_ROLES.USER,
    });

    useEffect(() => {
        setPage(1);
    }, [filters]);

    const openAddContractModal = () => {
        setModalOpen(true);
        setFormData(INITIAL_FORM_DATA);
        setIsUpdating(false);
    };

    const handleOpen = (value) => setOpen(open === value ? -1 : value);



    const handleDeleteContract = async (contract) => {
        const isConfirmed = await confirm({
            title: 'Cancel Contract Confirmation',
            footer: <div><span className="text-xs text-gray-400">Id: {contract._id}</span></div>,
            message: <div className="text-sm">
                <p className="text-red-500 font-normal">
                    You are about to cancel the contract for {contract.user?.name}.
                </p>

                <p>This action will:</p>
                <ul className="list-disc list-inside">
                    <li>Mark the contract as void in the system</li>
                    <li>Release all associated seats and offices back to the available inventory</li>
                    <li>Cancel all bookings related to this contract</li>
                    <li>Remove these resources from the user's profile</li>
                </ul>
                <strong>Do you want to proceed with cancellation?</strong>
                <p className="text-red-500 text-xs mt-2">This action cannot be undone. If the client needs these resources again, a new contract will need to be created.</p>
            </div>,
            confirmColor: 'red',
            confirmText: 'Confirm',
            cancelText: 'Cancel',
        });
        if (isConfirmed) {
            try {
                setLoadingDeleteContract(true);
                await DeleteContract(contract._id);
                Notify(`Contract for ${contract.user?.name} Cancelled`, 'info');
            } catch (error) {
                NotifyError('Error cancelling contract', error);
            } finally {
                setLoadingDeleteContract(false);
            }
        }
        return;
    }

    const handleTerminateContract = (contract) => {
        setContractDataToTerminate(contract);
        setTeminationModalOpen(true);
    }


    return (
        <Fragment>
            <div>
                <div className="m-5 p-5 border rounded-3xl space-y-4">
                    <PageHeader
                        title={`Manage Contracts - ${total}`}
                        isLoading={isContractsLoading}
                        actionOnClick={openAddContractModal}
                        actionType="add"
                        actionText="Add New Contract"
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                        {
                            isUsersError ?
                                <div className="flex justify-center">
                                    <div className="text-sm text-gray-500">Error fetching users</div>
                                </div>
                                :
                                <Autocomplete
                                    placeholder={"Select Customer"}
                                    isLoading={isUsersLoading}
                                    options={
                                        users?.users?.map((user) => ({
                                            value: user._id,
                                            label: <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-2'>
                                                <div className='flex items-center gap-2'>
                                                    <div className='w-4 h-4 rounded-full'>
                                                        <UserIcon size={12} />
                                                    </div>
                                                    {user.name}
                                                </div>
                                                <div className='text-xs text-gray-700'>
                                                    {user.email}
                                                </div>
                                            </div>,
                                            searchLabel: user.name + ' (' + user.email + ')'
                                        })) || []}
                                    isClearable={true}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.searchLabel}
                                    onChange={(newValue) => setFilters({ ...filters, user: newValue })}
                                    value={filters.user}

                                />
                        }
                        <Select
                            label="Filter by Status"
                            value={filters.status}
                            onChange={(value) => setFilters({ ...filters, status: value })}

                        >
                            {
                                VERIFICATION_TYPES_ENUM.map((status) => (
                                    <Option key={status} value={status}>
                                        <div className="flex items-center justify-between gap-2">
                                            {convertToPresentableText(status)}
                                            <span className="text-xs text-gray-500"><XIcon size={12} onClick={() => setFilters({ ...filters, status: '' })} /></span>
                                        </div>
                                    </Option>
                                ))
                            }
                        </Select>
                    </div>
                    <hr className="my-4" />
                    <div>
                        {
                            isContractsLoading ?
                                <div className="flex justify-center">
                                    <Spinner />
                                </div>
                                : isContractsError ?
                                    <div className="flex justify-center">
                                        <div className="text-sm text-gray-500">Error fetching contracts</div>
                                    </div>
                                    :
                                    contracts?.contracts?.map((contract, i) => (
                                        <Accordion icon={open === i ? <ChevronUpIcon /> : <ChevronDownIcon />} key={i} open={open === i} className="mb-2 rounded-lg border border-blue-gray-100 px-4">
                                            <AccordionHeader onClick={() => handleOpen(i)} className={`border-b-0 transition-colors ${open === i ? "text-blue-500 hover:!text-blue-700" : ""}`}>
                                                <div className="flex w-full justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="font-thin text-md">
                                                                {contract.user?.name || ``}
                                                            </div>
                                                            <div className="font-thin text-xs text-gray-500">
                                                                {moment(contract.startDate).format('ll')} - {contract.endDate ? moment(contract.endDate).format('ll') : `Open (${contract.noticePeriod} Weeks Notice)`}
                                                            </div>
                                                        </div>
                                                        <div className="font-thin text-xs">
                                                            {convertToPresentableText(contract.contractType)} For {contract.reason}
                                                        </div>
                                                    </div>
                                                    <div className="font-thin text-xs flex flex-col items-end gap-1">
                                                        {InterpretVerificationStatus(contract.status)}
                                                        <div>
                                                            {contract.bookings.length} Bookings
                                                        </div>
                                                    </div>
                                                </div>
                                            </AccordionHeader>
                                            <AccordionBody>
                                                <div className="text-lg">Bookings</div>
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rent</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {contract.bookings.map((booking) => (
                                                                <tr key={booking._id}>
                                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            {booking.office ? "Office" : "Seat"}
                                                                        </div>
                                                                        <div className="text-xs text-gray-500">
                                                                            {booking.office ?
                                                                                `${booking.office.code} (${booking.office.capacity} seats)` :
                                                                                `${booking.seat.seatCode} (${booking.seat.seatType})`}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                                        <div className="text-sm text-gray-900">
                                                                            {booking.office ?
                                                                                `${booking.office.floor.building.name}, Floor ${booking.office.floor.floorCode}` :
                                                                                `${booking.seat.coworking.floor.building.name}, Floor ${booking.seat.coworking.floor.floorCode}`}
                                                                        </div>
                                                                        <div className="text-xs text-gray-500">
                                                                            {booking.office ?
                                                                                booking.office.floor.building.branch.name :
                                                                                booking.seat.coworking.floor.building.branch.name} Branch
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                        {booking.dailyStartTime && booking.dailyEndTime ? (
                                                                            <div>
                                                                                <div>Daily: {moment(booking.dailyStartTime).format("h:mm A")} - {moment(booking.dailyEndTime).format("h:mm A")}</div>
                                                                                <div className="text-xs">From: {moment(booking.startDate).format("MMM DD, YYYY")} - {booking.endDate ? moment(booking.endDate).format("MMM DD, YYYY") : `${contract.noticePeriod} weeks notice`}</div>
                                                                            </div>
                                                                        ) : (
                                                                            <div>
                                                                                <div>Full-time access</div>
                                                                                <div className="text-xs">From: {moment(booking.startDate).format("MMM DD, YYYY")} - {booking.endDate ? moment(booking.endDate).format("MMM DD, YYYY") : "Open"}</div>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            {formatCurrency(booking.rentTotal)}
                                                                        </div>
                                                                        <div className="text-xs text-gray-500">
                                                                            {booking.rentFrequency}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                                        <div className="flex">
                                                                            {InterpretVerificationStatus(booking.status)}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <hr className="my-2" />
                                                <div className="flex items-center justify-between pe-3">
                                                    <div>
                                                        Total Bookings: {contract.bookings.length}<br />
                                                        <span className="text-xs text-gray-500">Id: {contract._id}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {
                                                            contract.status === VERIFICATION_TYPES.APPROVED &&
                                                            <div>
                                                                <Button className="rounded-full flex items-center gap-2" variant="outlined" color="amber" size="sm" onClick={() => handleTerminateContract(contract)} disabled={loadingDeleteContract}>
                                                                    {loadingDeleteContract ? <Spinner className="w-4 h-4" /> : <ClipboardClockIcon size={12} />} End Contract
                                                                </Button>
                                                            </div>

                                                        }
                                                        {
                                                            contract.status === VERIFICATION_TYPES.APPROVED &&
                                                            <div>
                                                                <Button className="rounded-full flex items-center gap-2" variant="outlined" color="red" size="sm" onClick={() => handleDeleteContract(contract)} disabled={loadingDeleteContract}>
                                                                    {loadingDeleteContract ? <Spinner className="w-4 h-4" /> : <ArchiveXIcon size={12} />} Delete
                                                                </Button>
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            </AccordionBody>
                                        </Accordion>
                                    ))
                        }
                    </div>
                    {
                        totalPages > 1 &&
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            limit={limit}
                            onChangePage={setPage}
                            onChangeLimit={setLimit}
                        />
                    }

                </div>
            </div>
            <ConfirmationDialog />
            <ContractFormModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                isUpdating={isUpdating}
                defaultData={formData}
            />
            {
                teminationModalOpen && contractDataToTerminate &&
                <ContractTerminateModal
                    isOpen={teminationModalOpen}
                    onClose={() => setTeminationModalOpen(false)}
                    data={contractDataToTerminate}
                />
            }
        </Fragment>
    );
};

function formatCurrency(amount) {
    return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
}
export default ContractPage;